import { useState, useEffect } from 'react';
import styles from './PedidoStatus.module.css';
import { FaGear, FaTruck, FaBoxOpen } from "react-icons/fa6";
import { FaMoneyBillWaveAlt } from "react-icons/fa";
import { getPedidoByIdRoyal } from './PedidoStatusApi';

export default function PedidoStatus({ pedidoId }) {
    const [pedido, setPedido] = useState(null);
    const [loadingPedido, setLoadingPedido] = useState(true);
    const [erroPedido, setErroPedido] = useState('');

    const [windowDimensions, setWindowDimensions] = useState({
        height: window.innerHeight,
        width: window.innerWidth,
    });

    useEffect(() => {
        function handleResize() {
            setWindowDimensions({
                height: window.innerHeight,
                width: window.innerWidth,
            });
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let mounted = true;

        async function loadPedido() {
            try {
                if (!pedidoId) {
                    throw new Error('pedido_id não informado');
                }

                setLoadingPedido(true);
                setErroPedido('');

                const data = await getPedidoByIdRoyal(pedidoId);

                if (mounted) {
                    setPedido(data.pedido);
                }
            } catch (error) {
                if (mounted) {
                    setErroPedido(error.message || 'Erro ao carregar pedido');
                }
            } finally {
                if (mounted) {
                    setLoadingPedido(false);
                }
            }
        }

        loadPedido();

        return () => {
            mounted = false;
        };
    }, [pedidoId]);

    const isMobile = windowDimensions.width < 992;
    const currentStage = pedido?.current_stage || 'pending_payment';

    const checkPay = [
        'paid',
        'production_started',
        'production_completed',
        'separation_started',
        'separation_completed',
        'shipped',
        'delivered'
    ].includes(currentStage);

    const checkProduction = [
        'production_completed',
        'separation_started',
        'separation_completed',
        'shipped',
        'delivered'
    ].includes(currentStage);

    const checkSend = [
        'shipped',
        'delivered'
    ].includes(currentStage);

    const checkReceive = currentStage === 'delivered';

    const etapas = [
        {
            nome: 'Pagamento',
            ativa: !checkPay,
            concluida: checkPay,
            icon: <FaMoneyBillWaveAlt className={styles.icon} />,
        },
        {
            nome: 'Produção',
            ativa: checkPay && !checkProduction,
            concluida: checkProduction,
            icon: <FaGear className={styles.icon} />,
        },
        {
            nome: 'Envio',
            ativa: checkProduction && !checkSend,
            concluida: checkSend,
            icon: <FaTruck className={styles.icon} />,
        },
        {
            nome: 'Entrega',
            ativa: checkSend && !checkReceive,
            concluida: checkReceive,
            icon: <FaBoxOpen className={styles.icon} />,
        },
    ];

    const etapaAtualIndex = currentStage === 'cancelled'
        ? 0
        : !checkPay
        ? 0
        : checkPay && !checkProduction
        ? 1
        : checkProduction && !checkSend
        ? 2
        : checkSend && !checkReceive
        ? 3
        : 4;

    const progresso = currentStage === 'cancelled'
        ? 0
        : checkReceive
        ? 100
        : ((etapaAtualIndex + 1) / 4) * 100;

    const statusAtualTitulo =
        currentStage === 'cancelled'
            ? 'Pedido cancelado'
            : !checkPay
            ? 'Em andamento analisando pagamento'
            : checkPay && !checkProduction
            ? 'Pedido em produção'
            : checkProduction && !checkSend
            ? 'Pedido em envio'
            : checkSend && !checkReceive
            ? 'Pedido em transporte'
            : 'Pedido entregue';

    const statusAtualDescricao =
        currentStage === 'cancelled'
            ? 'Este pedido foi cancelado. Caso precise, entre em contato para mais informações.'
            : !checkPay
            ? 'Seu pagamento está em análise. Assim que for confirmado, seu pedido seguirá automaticamente para a próxima etapa.'
            : checkPay && !checkProduction
            ? 'O pagamento foi confirmado com sucesso. Agora seu pedido está na etapa de produção.'
            : checkProduction && !checkSend
            ? 'Seu produto já foi produzido e está sendo preparado para envio.'
            : checkSend && !checkReceive
            ? 'Seu pedido já foi despachado e está em rota de entrega. Você receberá novas atualizações em breve.'
            : 'Seu pedido foi entregue com sucesso. Agradecemos pela confiança.';

    const statusBadge =
        currentStage === 'cancelled'
            ? 'Cancelado'
            : !checkPay
            ? 'Pedido em análise'
            : checkPay && !checkProduction
            ? 'Em produção'
            : checkProduction && !checkSend
            ? 'Em envio'
            : checkSend && !checkReceive
            ? 'Em transporte'
            : 'Entregue';

    const etapaTexto =
        currentStage === 'cancelled'
            ? 'Cancelado'
            : !checkPay
            ? 'Pagamento'
            : checkPay && !checkProduction
            ? 'Produção'
            : checkProduction && !checkSend
            ? 'Envio'
            : checkSend && !checkReceive
            ? 'Entrega'
            : 'Finalizado';

    const previsaoTexto =
        currentStage === 'cancelled'
            ? 'Processo encerrado'
            : !checkPay
            ? 'Confirmação do pagamento'
            : checkPay && !checkProduction
            ? 'Finalização da produção'
            : checkProduction && !checkSend
            ? 'Despacho do pedido'
            : checkSend && !checkReceive
            ? 'Entrega ao cliente'
            : 'Concluído';

    const proximaEtapa =
        currentStage === 'cancelled'
            ? 'Cancelado'
            : !checkPay
            ? 'Produção'
            : checkPay && !checkProduction
            ? 'Envio'
            : checkProduction && !checkSend
            ? 'Entrega'
            : checkSend && !checkReceive
            ? 'Conclusão'
            : 'Concluído';

    const processoPedidoPay =
        !checkPay
            ? 'Estamos confirmando as informações da transação.'
            : 'Pagamento confirmado com sucesso.';

    const processoPedidoProduction =
        !checkPay
            ? 'Seu produto entrará em fabricação após a confirmação do pagamento.'
            : checkPay && !checkProduction
            ? 'Seu produto está em processo de fabricação.'
            : 'Produção concluída com sucesso.';

    const processoPedidoSend =
        !checkProduction
            ? 'Seu pedido será preparado para envio assim que a produção for concluída.'
            : checkProduction && !checkSend
            ? 'Seu pedido está sendo separado e preparado para despacho.'
            : 'Pedido enviado com sucesso.';

    const processoPedidoOk =
        !checkSend
            ? 'Aguardando o envio do pedido para liberar as atualizações de entrega.'
            : checkSend && !checkReceive
            ? 'Seu pedido está em rota de entrega.'
            : 'Entrega concluída com sucesso.';

    const timelineSteps = [
        {
            title: 'Pagamento',
            text: processoPedidoPay,
            active: etapaAtualIndex >= 0,
            muted: false,
        },
        {
            title: 'Produção',
            text: processoPedidoProduction,
            active: etapaAtualIndex >= 1,
            muted: etapaAtualIndex < 1,
        },
        {
            title: 'Envio',
            text: processoPedidoSend,
            active: etapaAtualIndex >= 2,
            muted: etapaAtualIndex < 2,
        },
        {
            title: 'Entrega',
            text: processoPedidoOk,
            active: etapaAtualIndex >= 3,
            muted: etapaAtualIndex < 3,
        },
    ];

    if (loadingPedido) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <header className={styles.header}>
                        <div>
                            <h1 className={styles.title}>Acompanhar pedido</h1>
                        </div>

                        <div className={styles.badge}>
                            Carregando
                        </div>
                    </header>
                </div>
            </div>
        );
    }

    if (erroPedido) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    <header className={styles.header}>
                        <div>
                            <h1 className={styles.title}>Acompanhar pedido</h1>
                        </div>

                        <div className={styles.badge}>
                            Erro
                        </div>
                    </header>

                    <section className={styles.mainSection}>
                        <div className={styles.mainCard}>
                            <div className={styles.mainTop}>
                                <div>
                                    <p className={styles.cardKicker}>Status atual</p>
                                    <h2 className={styles.mainTitle}>Erro ao carregar pedido</h2>
                                    <p className={styles.mainDescription}>{erroPedido}</p>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <header className={styles.header}>
                    <div>
                        <h1 className={styles.title}>Acompanhar pedido</h1>
                    </div>

                    <div className={styles.badge}>
                        {statusBadge}
                    </div>
                </header>

                <section className={styles.stepsSection}>
                    <div className={styles.stepsGrid}>
                        {etapas.map((etapa, index) => (
                            <div key={etapa.nome} className={styles.stepItem}>
                                {index < etapas.length - 1 && !isMobile && (
                                    <div
                                        className={`${styles.stepLine} ${
                                            etapaAtualIndex > index + 1
                                                ? styles.stepLineDone
                                                : etapaAtualIndex === index + 1
                                                ? styles.stepLineActive
                                                : ''
                                        }`}
                                    />
                                )}

                                <div
                                    className={[
                                        styles.stepCircle,
                                        etapa.ativa
                                            ? styles.stepCircleActive
                                            : etapa.concluida
                                            ? styles.stepCircleDone
                                            : styles.stepCircleDefault,
                                    ].join(' ')}
                                >
                                    {etapa.ativa && <span className={styles.circleRing} />}
                                    {etapa.icon}
                                </div>

                                <span
                                    className={[
                                        styles.stepLabel,
                                        etapa.ativa ? styles.stepLabelActive : '',
                                    ].join(' ')}
                                >
                                    {etapa.nome}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className={styles.mainSection}>
                    <div className={styles.mainCard}>
                        <div className={styles.mainTop}>
                            <div>
                                <p className={styles.cardKicker}>Status atual</p>
                                <h2 className={styles.mainTitle}>{statusAtualTitulo}</h2>
                                <p className={styles.mainDescription}>{statusAtualDescricao}</p>
                            </div>

                            <div className={styles.processingBadge}>
                                <span className={styles.processingDot} />
                                {checkReceive ? 'Concluído' : currentStage === 'cancelled' ? 'Cancelado' : 'Processando'}
                            </div>
                        </div>

                        <div className={styles.infoGrid}>
                            <div className={styles.infoCard}>
                                <p className={styles.infoLabel}>Etapa</p>
                                <p className={styles.infoValue}>{etapaTexto}</p>
                            </div>

                            <div className={styles.infoCard}>
                                <p className={styles.infoLabel}>Previsão</p>
                                <p className={styles.infoValue}>{previsaoTexto}</p>
                            </div>

                            <div className={styles.infoCard}>
                                <p className={styles.infoLabel}>Ambiente</p>
                                <p className={styles.infoValue}>Transação segura</p>
                            </div>
                        </div>

                        <div className={styles.progressCard}>
                            <div className={styles.progressTop}>
                                <div>
                                    <p className={styles.progressTitle}>Progresso do pedido</p>
                                    <p className={styles.progressSub}>
                                        {checkReceive ? 4 : currentStage === 'cancelled' ? 0 : etapaAtualIndex + 1} de 4 etapas em andamento
                                    </p>
                                </div>

                                <p className={styles.progressPercent}>
                                    {Math.round(progresso)}%
                                </p>
                            </div>

                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${progresso}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    <aside className={styles.sidebar}>
                        <div className={styles.summaryCardDark}>
                            <p className={styles.summaryKicker}>Resumo</p>

                            <div className={styles.summaryList}>
                                <div className={styles.summaryRow}>
                                    <span>Pedido</span>
                                    <strong>#{String(pedido?.pedido_id || '').padStart(6, '0')}</strong>
                                </div>
                                <div className={styles.summaryRow}>
                                    <span>Status</span>
                                    <strong>{statusBadge}</strong>
                                </div>
                                <div className={styles.summaryRow}>
                                    <span>Próxima etapa</span>
                                    <strong>{proximaEtapa}</strong>
                                </div>
                                <div className={styles.summaryRowNoBorder}>
                                    <span>Canal</span>
                                    <strong>Online</strong>
                                </div>
                            </div>
                        </div>

                        <div className={styles.timelineCard}>
                            <p className={styles.summaryKickerLight}>Processos do pedido</p>

                            <div className={styles.timelineList}>
                                {timelineSteps.map((item) => (
                                    <div
                                        key={item.title}
                                        className={`${styles.timelineItem} ${
                                            item.muted ? styles.timelineItemMuted : ''
                                        }`}
                                    >
                                        <div
                                            className={`${styles.timelineDot} ${
                                                item.active ? styles.timelineDotActive : ''
                                            }`}
                                        />
                                        <div>
                                            <p className={styles.timelineTitle}>{item.title}</p>
                                            <p className={styles.timelineText}>{item.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>
                </section>
            </div>
        </div>
    );
}